import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Next step in onboarding process
 */
export interface OnboardingNextStep {
  step: string;
  required: boolean;
  url: string;
}

/**
 * Member onboarding progress tracking
 *
 * Reference: agent-os/specs/2026-01-22-user-onboarding/spec.md
 * Database: adn_member_onboarding_progress table
 */
export interface MemberOnboardingProgress {
  profileCompleted: boolean;
  part107Uploaded: boolean;
  medicalUploaded: boolean;
  insuranceUploaded: boolean;
  stateLicenseUploaded: boolean;
  safetyGuidelinesReviewed: boolean;
  completedAt: string | null;
  percentComplete: number;
  nextSteps: OnboardingNextStep[];      // Required steps remaining
  optionalSteps?: OnboardingNextStep[]; // Optional steps remaining
}

/**
 * @deprecated Replaced by UnifiedOnboardingService for progress display as of 2026-02-13.
 * Note: OnboardingWizardModalComponent still uses this service for step processing
 * (markProfileCompleted, markSafetyReviewed). Do NOT remove until the wizard is also refactored.
 * See: agent-os/specs/2026-02-13-unified-onboarding-redesign/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class MemberOnboardingProgressService {
  private apiUrl = environment.apiUrl;
  private http = inject(HttpClient);

  /**
   * Get onboarding progress for current user in specific organization
   *
   * @param organizationId Organization ID
   * @returns Promise with onboarding progress
   */
  async getProgress(organizationId: string): Promise<MemberOnboardingProgress> {
    const response = this.http.get<ApiResponse<{ progress: MemberOnboardingProgress }>>(
      `${this.apiUrl}/organizations/${organizationId}/onboarding/progress`
    );

    const result = await firstValueFrom(response);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to get onboarding progress');
    }

    return result.data.progress;
  }

  /**
   * Mark profile as completed
   *
   * @param organizationId Organization ID
   * @returns Promise indicating completion
   */
  async markProfileCompleted(organizationId: string): Promise<void> {
    const response = this.http.post<ApiResponse<void>>(
      `${this.apiUrl}/organizations/${organizationId}/onboarding/profile-completed`,
      {}
    );

    const result = await firstValueFrom(response);

    if (!result.success) {
      throw new Error(result.error || 'Failed to mark profile as completed');
    }
  }

  /**
   * Mark safety guidelines as reviewed
   *
   * @param organizationId Organization ID
   * @returns Promise indicating completion
   */
  async markSafetyReviewed(organizationId: string): Promise<void> {
    const response = this.http.post<ApiResponse<void>>(
      `${this.apiUrl}/organizations/${organizationId}/onboarding/safety-reviewed`,
      {}
    );

    const result = await firstValueFrom(response);

    if (!result.success) {
      throw new Error(result.error || 'Failed to mark safety guidelines as reviewed');
    }
  }

  /**
   * Calculate if onboarding is complete
   * Required: profile, Part 107, State Applicator License, safety guidelines
   *
   * @param progress Onboarding progress object
   * @returns True if all required items completed
   */
  isOnboardingComplete(progress: MemberOnboardingProgress): boolean {
    return (
      progress.profileCompleted &&
      progress.part107Uploaded &&
      progress.stateLicenseUploaded &&
      progress.safetyGuidelinesReviewed
    );
  }

  /**
   * Get remaining required items
   *
   * @param progress Onboarding progress object
   * @returns Array of incomplete required items
   */
  getRemainingRequiredItems(progress: MemberOnboardingProgress): string[] {
    const remaining: string[] = [];

    if (!progress.profileCompleted) {
      remaining.push('Complete Profile');
    }
    if (!progress.part107Uploaded) {
      remaining.push('Upload Part 107 Certificate');
    }
    if (!progress.stateLicenseUploaded) {
      remaining.push('Upload State Applicator License');
    }
    if (!progress.safetyGuidelinesReviewed) {
      remaining.push('Review Safety Guidelines');
    }

    return remaining;
  }

  /**
   * Get remaining optional items
   *
   * @param progress Onboarding progress object
   * @returns Array of incomplete optional items
   */
  getRemainingOptionalItems(progress: MemberOnboardingProgress): string[] {
    const remaining: string[] = [];

    if (!progress.medicalUploaded) {
      remaining.push('Upload Medical Certificate');
    }
    if (!progress.insuranceUploaded) {
      remaining.push('Upload Insurance Certificate');
    }

    return remaining;
  }
}
